import Pagination from "@/components/common/table/pagination";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  TableData,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";
import type { UserType } from "@/types/entities/users";
import dayjs from "dayjs";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";

const columnHelper = createColumnHelper<UserType>();

const columnDef = ({
  onUnassign,
}: {
  onUnassign: (userId: string) => void;
}) => {
  return [
    columnHelper.accessor("name", {
      id: "name",
      header: "Nombre",
      sortUndefined: "last",
      cell: (info) => {
        // if (!info.getValue()) {
        //   return <p>&nbsp;</p>;
        // }
        const { name, last_name } = info.row.original;
        return (
          <TableLeadingCell
            title={`${name || ""} ${last_name || ""}`.trim() || "-"}
          />
        );
      },
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Fecha de registro",
      sortUndefined: "last",
      meta: {
        className: "hidden sm:table-cell",
      },
      cell: (info) => {
        if (!info.getValue()) return "";
        return (
          <span className="text-body leading-3xl font-normal">
            {dayjs(info.getValue()).format("MMM DD, YYYY | HH:mm A")}
          </span>
        );
      },
    }),
    columnHelper.accessor("createdBy", {
      id: "createdBy",
      header: "Registrado por",
      sortUndefined: "last",
      meta: {
        className: "hidden xs:table-cell",
      },
      cell: (info) => {
        if (!info.getValue()) return "";
        const user = info.getValue();
        const fallback = user ? user?.name?.charAt(0).toUpperCase() : "U";
        return (
          <div className="flex gap-4 items-center">
            <Avatar size="xs">
              <AvatarFallback color="action">{fallback}</AvatarFallback>
            </Avatar>
            <p className="text-body font-semibold">
              {`${user?.name || ""} ${user?.last_name || ""}`.trim()}
            </p>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      sortUndefined: "last",
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;
        if (!user.id) {
          return <p>&nbsp;</p>;
        }
        return (
          <div className="w-full flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Desasignar usuario"
              type="button"
              onClick={() => {
                onUnassign(user.keycloak_id || "");
              }}
            >
              <Icon
                type="far"
                name="times"
                className="text-current text-base leading-3xl"
              />
            </Button>
          </div>
        );
      },
    }),
  ] as ColumnDef<UserType>[];
};

interface Props {
  data: UserType[];
  // roleId: string;
  isLoading?: boolean;
  onUnassign: (userId: string) => void;
}

const PAGE_SIZE = 20;

const compareCreatedByNames = (
  aName: string | null | undefined,
  bName: string | null | undefined,
  desc: boolean,
): number => {
  const aStr = aName || "";
  const bStr = bName || "";
  if (aStr === bStr) return 0;
  if (!aStr || !bStr) return 0;
  if (desc) {
    return aStr > bStr ? -1 : 1;
  }
  return aStr < bStr ? -1 : 1;
};

const compareValues = <T extends string | number | Date>(
  aValue: T | undefined | null,
  bValue: T | undefined | null,
  desc: boolean,
): number => {
  if (aValue === null && bValue === null) return 0;
  if (aValue === bValue) return 0;
  if (!aValue || !bValue) return 0;
  if (desc) {
    return aValue > bValue ? -1 : 1;
  }
  return aValue < bValue ? -1 : 1;
};

const sortUsersByField = (
  a: UserType,
  b: UserType,
  fieldId: string,
  desc: boolean,
): number => {
  if (fieldId === "createdBy") {
    const aCreatedBy = a.createdBy;
    const bCreatedBy = b.createdBy;
    if (!aCreatedBy && !bCreatedBy) return 0;
    return compareCreatedByNames(aCreatedBy?.name, bCreatedBy?.name, desc);
  }

  const aValue = a[fieldId as "name" | "created_at"];
  const bValue = b[fieldId as "name" | "created_at"];
  return compareValues(aValue, bValue, desc);
};

const UsersTable = ({ data, isLoading, onUnassign }: Props) => {
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const localPagination = useMemo(() => {
    const sortedData = data.toSorted((a, b) => {
      if (sorting.length === 0) return 0;
      const { id, desc } = sorting[0];
      return sortUsersByField(a, b, id, desc ?? false);
    });

    return {
      data: sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
      meta: {
        total: data.length,
        page: 1,
        per_page: PAGE_SIZE,
      },
    };
  }, [page, data, sorting]);

  useEffect(() => {
    if (data.length > 0 && localPagination.data.length === 0 && page > 0) {
      setPage((p) => p - 1);
    }
  }, [
    data.length,
    page,
    localPagination.data.length,
    localPagination.meta.total,
    sorting,
  ]);

  useEffect(() => {
    if (sorting.length === 1) {
      setPage(0); // Reset to first page when sorting changes
    }
  }, [sorting]);

  const columns = useMemo(() => {
    return columnDef({
      onUnassign: (userId: string) => {
        onUnassign(userId);
      },
    });
  }, [onUnassign]);

  if (isLoading) {
    return (
      <div className="h-screen w-full">
        <div className="bg-neutral-object border-y border-neutral w-full h-2" />
        <div className="w-full h-full flex justify-center items-center">
          <Icon
            type="far"
            name="spinner-third"
            className="animate-spin text-on-disabled text-xl"
            aria-hidden="true"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <TableData
        columns={columns}
        data={localPagination.data || []}
        stripped
        sorting={sorting}
        onSortingChange={setSorting}
      />
      <div className="p-3">
        <Pagination
          currentPage={page + 1}
          totalPages={Math.ceil(data.length / PAGE_SIZE)}
          hasNext={page + 1 < Math.ceil(data.length / PAGE_SIZE)}
          hasPrevious={page > 0}
          onPageChange={(pages) => {
            setPage(pages - 1); // Adjusting for zero-based index
          }}
          onNext={() => {
            if (page + 1 < Math.ceil(data.length / PAGE_SIZE)) {
              setPage((prev) => prev + 1);
            }
          }}
          onPrevious={() => {
            if (page > 0) {
              setPage((prev) => prev - 1);
            }
          }}
          pageSize={localPagination.data.length}
          totalItems={localPagination.meta.total}
        />
      </div>
    </>
  );
};

export default UsersTable;
